import React, { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";

// Landing is eagerly imported (first page users see, no lazy-flash needed)
import Landing from "./pages/Landing";
import MaintenancePage from "./pages/MaintenancePage";
import { useSiteSettings } from "./hooks/useSiteSettings";
// Lazy-load all other pages
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Playground = lazy(() => import("./pages/Playground"));
const AsyncJobsPage = lazy(() => import("./pages/AsyncJobsPage"));
const Analytics = lazy(() => import("./pages/Analytics"));
const UserErrorLogs = lazy(() => import("./pages/UserErrorLogs"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const Proxies = lazy(() => import("./pages/Proxies"));
const Domains = lazy(() => import("./pages/Domains"));
const Billing = lazy(() => import("./pages/Billing"));
const Settings = lazy(() => import("./pages/Settings"));
const Team = lazy(() => import("./pages/Team"));
const Docs = lazy(() => import("./pages/Docs"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback"));
const GithubCallback = lazy(() => import("./pages/GithubCallback"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ConfirmEmailChange = lazy(() => import("./pages/ConfirmEmailChange"));
const JoinWorkspace = lazy(() => import("./pages/JoinWorkspace"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CancellationRefundPolicy = lazy(() => import("./pages/CancellationRefundPolicy"));
const ShippingDelivery = lazy(() => import("./pages/ShippingDelivery"));
const PublicDocs = lazy(() => import("./pages/PublicDocs"));
const PublicChangelog = lazy(() => import("./pages/PublicChangelog"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const AUP = lazy(() => import("./pages/AUP"));
const Security = lazy(() => import("./pages/Security"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const ApiUsagePolicy = lazy(() => import("./pages/ApiUsagePolicy"));
const LegalNotices = lazy(() => import("./pages/LegalNotices"));
const DataProcessingAgreement = lazy(() => import("./pages/DataProcessingAgreement"));
const GDPR = lazy(() => import("./pages/GDPR"));
// Admin pages
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminAccountSettings = lazy(() => import("./pages/admin/AdminAccountSettings"));
const AdminBilling = lazy(() => import("./pages/admin/AdminBilling"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminWebhooks = lazy(() => import("./pages/admin/AdminWebhooks"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminContactInfoEditor = lazy(() => import("./pages/admin/AdminContactInfoEditor"));
const AdminEmailLogs = lazy(() => import("./pages/admin/AdminEmailLogs"));
const AdminSubscriptionAnalytics = lazy(() => import("./pages/admin/AdminSubscriptionAnalytics"));
const AdminBroadcast = lazy(() => import("./pages/admin/AdminBroadcast"));
const AdminUserProfile = lazy(() => import("./pages/admin/AdminUserProfile"));
const AdminAPIKeys = lazy(() => import("./pages/admin/AdminAPIKeys"));
const AdminRateLimits = lazy(() => import("./pages/admin/AdminRateLimits"));
const AdminSystem = lazy(() => import("./pages/admin/AdminSystem"));
const AdminMessaging = lazy(() => import("./pages/admin/AdminMessaging"));
const AdminErrorLogs = lazy(() => import("./pages/admin/AdminErrorLogs"));
const AdminSystemLogs = lazy(() => import("./pages/admin/AdminSystemLogs"));
const AdminWorkspaces = lazy(() => import("./pages/admin/AdminWorkspaces"));
const AdminWorkspaceDetail = lazy(() => import("./pages/admin/AdminWorkspaceDetail"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminBlogAnalytics = lazy(() => import("./pages/admin/AdminBlogAnalytics"));
const AdminCMSPages = lazy(() => import("./pages/admin/AdminCMSPages"));
const AdminCMSPageEditor = lazy(() => import("./pages/admin/AdminCMSPageEditor"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminAuthors = lazy(() => import("./pages/admin/AdminAuthors"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminSiteSettings = lazy(() => import("./pages/admin/AdminSiteSettings"));
const AdminDocsEditor = lazy(() => import("./pages/admin/AdminDocsEditor"));
const AdminChangelogEditor = lazy(() => import("./pages/admin/AdminChangelogEditor"));
const AdminPlansManager = lazy(() => import("./pages/admin/AdminPlansManager"));
const AdminTeamDirectory = lazy(() => import("./pages/admin/AdminTeamDirectory"));
const AdminRolesManager = lazy(() => import("./pages/admin/AdminRolesManager"));
const AdminTeamMemberProfile = lazy(() => import("./pages/admin/AdminTeamMemberProfile"));
const AdminSecurityDashboard = lazy(() => import("./pages/admin/AdminSecurityDashboard"));
const AdminTeamNotes = lazy(() => import("./pages/admin/AdminTeamNotes"));
const AdminInviteAccept = lazy(() => import("./pages/admin/AdminInviteAccept"));
const AdminCompliance = lazy(() => import("./pages/admin/AdminCompliance"));
const AdminDevices = lazy(() => import("./pages/admin/AdminDevices"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/AdminEmailTemplates"));
const AdminInfrastructure = lazy(() => import("./pages/admin/AdminInfrastructure"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogList = lazy(() => import("./pages/BlogList"));
const CMSPublicPage = lazy(() => import("./pages/CMSPublicPage"));
const MessageCenter = lazy(() => import("./pages/MessageCenter"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const DMCA = lazy(() => import("./pages/DMCA"));
const CoreTeam = lazy(() => import("./pages/CoreTeam"));

/** Injects admin-configured verification/tracking scripts into <head> and <body>. */
function SiteScripts() {
  const { settings } = useSiteSettings();
  const v = settings?.verification || {};

  useEffect(() => {
    const injected = [];

    const injectHtml = (html, parent) => {
      if (!html || !html.trim()) return;
      const tpl = document.createElement("template");
      tpl.innerHTML = html;
      Array.from(tpl.content.childNodes).forEach(node => {
        let el;
        if (node.nodeName === "SCRIPT") {
          el = document.createElement("script");
          Array.from(node.attributes).forEach(a => el.setAttribute(a.name, a.value));
          el.textContent = node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          el = node.cloneNode(true);
        }
        if (el) { parent.appendChild(el); injected.push({ parent, el }); }
      });
    };

    injectHtml(v.head_html, document.head);
    injectHtml(v.body_start_html, document.body);
    injectHtml(v.body_end_html, document.body);

    return () => {
      injected.forEach(({ parent, el }) => { try { parent.removeChild(el); } catch {} });
    };
  }, [v.head_html, v.body_start_html, v.body_end_html]);

  return null;
}

function PageLoader() {
  return (
    <div className="min-h-screen bg-bg-0 flex items-center justify-center">
      <div className="text-[#00ff88] font-mono text-sm animate-pulse">loading...</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-accent font-mono">
        <span className="cursor">loading session</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  useEffect(() => {
    // Check maintenance on app load
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/status`)
      .then(r => r.json())
      .then(data => { setMaintenance(!!data.maintenance_mode); setMaintenanceChecked(true); })
      .catch(() => setMaintenanceChecked(true));

    // Listen for maintenance events from API interceptor
    const handler = () => setMaintenance(true);
    window.addEventListener("scrapecore:maintenance", handler);
    return () => window.removeEventListener("scrapecore:maintenance", handler);
  }, []);

  if (!maintenanceChecked) return <PageLoader />;

  // Show maintenance page for all non-admin routes
  if (maintenance) {
    const isAdminPath = window.location.pathname.startsWith("/admin");
    if (!isAdminPath) return <MaintenancePage />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
      <Route path="/join-workspace" element={<JoinWorkspace />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/auth/github" element={<GithubCallback />} />

      {/* User Dashboard */}
      <Route
        path="/dashboard"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="playground" element={<PrivateRoute><Playground /></PrivateRoute>} />
        <Route path="async-jobs" element={<PrivateRoute><AsyncJobsPage /></PrivateRoute>} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="errors" element={<UserErrorLogs />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="proxies" element={<Proxies />} />
        <Route path="domains" element={<Domains />} />
        <Route path="billing" element={<Billing />} />
        <Route path="team" element={<Team />} />
        <Route path="messages" element={<MessageCenter />} />
        <Route path="settings" element={<Settings />} />
        <Route path="docs" element={<Docs />} />
      </Route>

      {/* Admin Panel */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="account" element={<AdminAccountSettings />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:userId" element={<AdminUserProfile />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="plans" element={<AdminPlansManager />} />
        <Route path="broadcast" element={<AdminBroadcast />} />
        <Route path="messaging" element={<AdminMessaging />} />
        <Route path="api-keys" element={<AdminAPIKeys />} />
        <Route path="rate-limits" element={<AdminRateLimits />} />
        <Route path="system" element={<AdminSystem />} />
        <Route path="system-logs" element={<AdminSystemLogs />} />
        <Route path="error-logs" element={<AdminErrorLogs />} />
        <Route path="analytics" element={<AdminSubscriptionAnalytics />} />
        <Route path="email-logs" element={<AdminEmailLogs />} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
        <Route path="webhooks" element={<AdminWebhooks />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="workspaces" element={<AdminWorkspaces />} />
        <Route path="workspaces/:workspaceId" element={<AdminWorkspaceDetail />} />
        {/* CMS & Blog */}
        <Route path="blog" element={<AdminBlog />} />
        <Route path="blog/new" element={<AdminBlogEditor />} />
        <Route path="blog/:postId/edit" element={<AdminBlogEditor />} />
        <Route path="blog/analytics" element={<AdminBlogAnalytics />} />
        <Route path="blog/authors" element={<AdminAuthors />} />
        <Route path="blog/categories" element={<AdminCategories />} />
        <Route path="cms" element={<AdminCMSPages />} />
        <Route path="cms/:slug" element={<AdminCMSPageEditor />} />
        <Route path="media" element={<AdminMedia />} />
        <Route path="settings" element={<AdminSiteSettings />} />
        <Route path="docs" element={<AdminDocsEditor />} />
        <Route path="changelog" element={<AdminChangelogEditor />} />
        <Route path="contact-info" element={<AdminContactInfoEditor />} />
        <Route path="team" element={<AdminTeamDirectory />} />
        <Route path="team/:memberId" element={<AdminTeamMemberProfile />} />
        <Route path="roles" element={<AdminRolesManager />} />
        <Route path="security" element={<AdminSecurityDashboard />} />
        <Route path="notes" element={<AdminTeamNotes />} />
        <Route path="compliance" element={<AdminCompliance />} />
        <Route path="devices" element={<AdminDevices />} />
        <Route path="email-templates" element={<AdminEmailTemplates />} />
        <Route path="infrastructure" element={<AdminInfrastructure />} />
      </Route>

      {/* Public admin invite accept — outside AdminRoute, no auth needed */}
      <Route path="/admin/invite/accept/:token" element={<AdminInviteAccept />} />

      {/* Legal / Company pages — now CMS-managed */}
      <Route path="/privacy" element={<CMSPublicPage slug="privacy-policy" />} />
      <Route path="/terms" element={<CMSPublicPage slug="terms-of-service" />} />
      <Route path="/refund-policy" element={<CMSPublicPage slug="cancellation-refund-policy" />} />
      <Route path="/cancellation-refund-policy" element={<CMSPublicPage slug="cancellation-refund-policy" />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<CMSPublicPage slug="about-us" />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/aup" element={<CMSPublicPage slug="aup" />} />
      <Route path="/security" element={<CMSPublicPage slug="security-policy" />} />
      <Route path="/cookie-policy" element={<CMSPublicPage slug="cookie-policy" />} />
      <Route path="/usage-policy" element={<CMSPublicPage slug="api-usage-policy" />} />
      <Route path="/legal-notices" element={<CMSPublicPage slug="legal-notices" />} />
      <Route path="/dpa" element={<CMSPublicPage slug="dpa" />} />
      <Route path="/gdpr" element={<CMSPublicPage slug="gdpr" />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/dmca" element={<DMCA />} />
      <Route path="/team" element={<CoreTeam />} />
      <Route path="/shipping-delivery" element={<CMSPublicPage slug="shipping-delivery" />} />
      <Route path="/docs" element={<PublicDocs />} />
      <Route path="/changelog" element={<PublicChangelog />} />
      {/* Public Blog */}
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* CMS Public Pages */}
      <Route path="/pages/:slug" element={<CMSPublicPage />} />
      <Route path="/privacy-policy" element={<CMSPublicPage slug="privacy-policy" />} />
      <Route path="/terms-and-conditions" element={<CMSPublicPage slug="terms-of-service" />} />
      <Route path="/cancellation-and-refund-policy" element={<CMSPublicPage slug="cancellation-refund-policy" />} />
      <Route path="/shipping-and-delivery-policy" element={<CMSPublicPage slug="shipping-delivery" />} />
      <Route path="/about-us" element={<CMSPublicPage slug="about-us" />} />
      <Route path="/contact-us" element={<CMSPublicPage slug="contact-us" />} />
      <Route path="/security-policy" element={<CMSPublicPage slug="security-policy" />} />
      <Route path="/cookie-policy" element={<CMSPublicPage slug="cookie-policy" />} />
      <Route path="/api-usage-policy" element={<CMSPublicPage slug="api-usage-policy" />} />
      <Route path="/acceptable-use-policy" element={<CMSPublicPage slug="aup" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <SiteScripts />
            <Toaster
              position="top-right"
              theme="dark"
              toastOptions={{
                style: {
                  background: "#111115",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  borderRadius: "10px",
                  fontSize: "14px",
                },
              }}
            />
            <Suspense fallback={<PageLoader />}>
              <AppRoutes />
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}
